// app/routes/__root.tsx
import {
  createRootRouteWithContext,
  Outlet,
  useLocation,
} from "@tanstack/react-router";
import { TanStackRouterDevtools } from "@tanstack/router-devtools";
import { TopNav } from "~/components/core/TopNav";
import { AppSidebar } from "~/components/core/sidebar/sidebar";
import { SidebarProvider } from "~/components/ui/sidebar";
import { useAuthenticatedUser } from "~/hooks/useAuthenticatedUser";
import { useAuth0Login } from "~/hooks/useAuth0Login";
import { useTenant } from "~/hooks/useTenant";
import { Spinner } from "~/components/ui/spinner";
import { getTenantSlugFromPathname } from "~/lib/tenant-routing";
import type { RouterContext } from "~/router";

const PUBLIC_ROUTES = new Set([
  "/",
  "/login",
  "/signup",
  "/declaration-invitation",
]);
const SKIP_LAYOUT_PREFIXES = ["/apply"];
const SKIP_LAYOUT_ROUTES = ["/completed-application"];
const SHOW_SIDEBAR_PREFIXES = ["/apply/how-to-use"];

/**
 * Strip the tenant slug prefix from a pathname for route matching.
 * e.g. /uni1/dashboard → /dashboard, /uni1/apply/123 → /apply/123
 */
function getEffectivePathname(pathname: string): string {
  const tenantSlug = getTenantSlugFromPathname(pathname);
  if (tenantSlug && pathname.startsWith(`/${tenantSlug}`)) {
    const stripped = pathname.slice(`/${tenantSlug}`.length);
    return stripped || "/";
  }
  return pathname;
}

export const Route = createRootRouteWithContext<RouterContext>()({
  component: Root,
});

function Root() {
  const location = useLocation();
  const {
    user,
    isLoading: isUserLoading,
    isUniversityAccount,
    organizationSlug,
  } = useAuthenticatedUser();
  const { isLoading: isAuthLoading, isAuthenticated } = useAuth0Login();
  const tenant = useTenant();
  const pathname = getEffectivePathname(location.pathname);

  const hasTenantMismatch =
    tenant.isTenantPortal &&
    !tenant.isLoading &&
    !tenant.error &&
    isAuthenticated &&
    !!user &&
    (isUniversityAccount
      ? tenant.slug !== organizationSlug
      : true);
  const isTenantGuardRoute =
    tenant.isTenantPortal &&
    (tenant.isLoading || !!tenant.error || hasTenantMismatch);

  const isSkipLayoutRoute =
    (SKIP_LAYOUT_PREFIXES.some(
      (prefix) => pathname === prefix || pathname.startsWith(prefix + "/")
    ) &&
      !SHOW_SIDEBAR_PREFIXES.some(
        (prefix) => pathname === prefix || pathname.startsWith(prefix + "/")
      )) ||
    SKIP_LAYOUT_ROUTES.includes(pathname) ||
    isTenantGuardRoute;

  if (isSkipLayoutRoute) {
    return <Outlet />;
  }

  const isPublicRoute = PUBLIC_ROUTES.has(pathname);
  const isLoading = isAuthLoading || isUserLoading;

  const showSidebar = Boolean(user) && !isPublicRoute;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <Spinner className="size-8 text-brand-green-primary" />
      </div>
    );
  }

  if (showSidebar) {
    return (
      <div className="min-h-screen flex flex-col overflow-x-hidden">
        <SidebarProvider>
          <TopNav />
          <div className="pt-16 flex flex-1 min-w-0 overflow-x-hidden">
            <AppSidebar />
            <main className="flex-1 min-w-0 bg-gray-50 overflow-y-auto overflow-x-hidden h-[calc(100vh-4rem)]">
              <Outlet />
            </main>
          </div>
        </SidebarProvider>
        {import.meta.env.DEV && <TanStackRouterDevtools />}
      </div>
    );
  }

  return (
    <SidebarProvider>
      <div className="min-h-screen flex flex-col w-full">
        <TopNav />
        <main className="flex-1">
          <Outlet />
        </main>
        {import.meta.env.DEV && <TanStackRouterDevtools />}
      </div>
    </SidebarProvider>
  );
}

export default Root;
